/**
 * Village Master seed — Mundra Taluka, Kachchh, Gujarat (Census 2011).
 * Sources:
 *  - https://gujarat.villagecodes.in/kachchh/mundra-46803730/
 *  - https://vill.co.in/gujarat/kachchh/mundra-468037300/
 * Map pin = official boundary centroid (DataMeet / Gujarat village maps).
 * Users do not type lat/lng.
 */

import { kachchhVillageBoundaries } from './kachchhVillageBoundaries';

export const VILLAGE_CATEGORIES = ['Rural', 'Urban fringe', 'Port belt', 'Industrial belt'];

const MUNDRA_BASE = {
  talukaName: 'Mundra',
  district: 'Kachchh',
  state: 'Gujarat',
  country: 'India',
  active: true,
};

/** Normalize names for fuzzy match (Moti Bhujpar ↔ MotiBhujpar). */
export function normalizeVillageName(name) {
  return String(name || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');
}

const byTalukaAndName = new Map();
const byName = new Map();

kachchhVillageBoundaries.forEach((row) => {
  const n = normalizeVillageName(row.name);
  const t = normalizeVillageName(row.taluka);
  byTalukaAndName.set(`${t}|${n}`, row);
  // Prefer Mundra when same name appears in multiple talukas
  if (!byName.has(n) || normalizeVillageName(row.taluka) === 'mundra') {
    byName.set(n, row);
  }
});

/**
 * Official Kachchh boundary + centroid.
 * Pass taluka when possible (e.g. Anjar / Mundra) to avoid name collisions.
 */
export function lookupOfficialVillageGeo(villageName, talukaName) {
  if (!villageName?.trim()) return null;
  const n = normalizeVillageName(villageName);
  const t = normalizeVillageName(talukaName);
  const row = (t && byTalukaAndName.get(`${t}|${n}`)) || byName.get(n);
  if (!row) return null;
  return {
    latitude: row.latitude,
    longitude: row.longitude,
    boundary: row.boundary,
    sourceName: row.name,
    taluka: row.taluka,
    censusCode2001: row.censusCode2001,
  };
}

/**
 * Inhabited Mundra villages (Census 2011) + Mundra CT.
 * PIN / Gram Panchayat from Census village directory + vill.co.in.
 * Uninhabited omitted: Bharudiya, Pratappar (2), Sukhpar, Pragpar (2).
 */
const villageNames = [
  { name: 'Babiya', pin: '370435', gramPanchayat: 'Beraja', category: 'Rural' },
  { name: 'Bagda', pin: '370465', gramPanchayat: 'Bagda', category: 'Rural' },
  { name: 'Baraya', pin: '370415', gramPanchayat: 'Samagoga', category: 'Rural' },
  { name: 'Baroi', pin: '370421', gramPanchayat: 'Baroi', category: 'Port belt' },
  { name: 'Beraja', pin: '370405', gramPanchayat: 'Beraja', category: 'Rural' },
  { name: 'Bhadresar', pin: '370410', gramPanchayat: 'Bhadresar', category: 'Rural' },
  { name: 'Bhorara', pin: '370410', gramPanchayat: 'Bhorara', category: 'Rural' },
  { name: 'Bocha', pin: '370425', gramPanchayat: 'Karaghogha', category: 'Rural' },
  { name: 'Borana', pin: '370415', gramPanchayat: 'Nana Kapaya', category: 'Rural' },
  { name: 'Chhasra', pin: '370410', gramPanchayat: 'Chhasra', category: 'Rural' },
  { name: 'Depa', pin: '370435', gramPanchayat: 'Depa', category: 'Rural' },
  { name: 'Deshalpar', pin: '370405', gramPanchayat: 'Deshalpar', category: 'Rural' },
  { name: 'Dhrab', pin: '370421', gramPanchayat: 'Dhrab', category: 'Port belt' },
  { name: 'Fachariya', pin: '370425', gramPanchayat: 'Vagura', category: 'Rural' },
  { name: 'Gelda', pin: '370405', gramPanchayat: 'Gelda', category: 'Rural' },
  { name: 'Goersama', pin: '370145', gramPanchayat: 'Baroi', category: 'Rural' },
  { name: 'Gundala', pin: '370410', gramPanchayat: 'Gundala', category: 'Rural' },
  { name: 'Hatdi', pin: '370410', gramPanchayat: 'Hatdi', category: 'Rural' },
  { name: 'Jarpara', pin: '370405', gramPanchayat: 'Jarpara', category: 'Rural' },
  { name: 'Kanajra', pin: '370435', gramPanchayat: 'Kanajra', category: 'Rural' },
  { name: 'Kandagara Mota', pin: '370435', gramPanchayat: 'Kandagara Mota', category: 'Rural' },
  { name: 'Kandagara Nana', pin: '370425', gramPanchayat: 'Kandagara Nana', category: 'Rural' },
  { name: 'Karagoga', pin: '370415', gramPanchayat: 'Karagoga', category: 'Rural' },
  { name: 'Khakhar Moti', pin: '370435', gramPanchayat: 'Khakhar Moti', category: 'Rural' },
  { name: 'Kukadsar', pin: '370410', gramPanchayat: 'Kukadsar', category: 'Rural' },
  { name: 'Kundrodi', pin: '370410', gramPanchayat: 'Kundrodi', category: 'Rural' },
  { name: 'Kuvay', pin: '370410', gramPanchayat: 'Kuvay', category: 'Rural' },
  { name: 'Lakhapar', pin: '370425', gramPanchayat: 'Lakhapar', category: 'Rural' },
  { name: 'Lifara', pin: '370435', gramPanchayat: 'Lifara', category: 'Rural' },
  { name: 'Luni', pin: '370410', gramPanchayat: 'Luni', category: 'Rural' },
  { name: 'Mangra', pin: '370415', gramPanchayat: 'Mangra', category: 'Rural' },
  { name: 'Mokha', pin: '370410', gramPanchayat: 'Mokha', category: 'Rural' },
  { name: 'Mota Kapaya', pin: '370415', gramPanchayat: 'Mota Kapaya', category: 'Rural' },
  { name: 'Moti Bhujpar', pin: '370405', gramPanchayat: 'Moti Bhujpar', category: 'Rural' },
  { name: 'Nana Kapaya', pin: '370415', gramPanchayat: 'Nana Kapaya', category: 'Industrial belt' },
  { name: 'Nani Bhujpar', pin: '370405', gramPanchayat: 'Nani Bhujpar', category: 'Rural' },
  { name: 'Navinal', pin: '370405', gramPanchayat: 'Navinal', category: 'Industrial belt' },
  { name: 'Patri', pin: '370425', gramPanchayat: 'Patri', category: 'Rural' },
  { name: 'Pavdiara', pin: '370410', gramPanchayat: 'Pavdiara', category: 'Rural' },
  { name: 'Pragpar', pin: '370155', gramPanchayat: 'Pragpar', category: 'Rural' },
  { name: 'Pratappar', pin: '370405', gramPanchayat: 'Pratappar', category: 'Rural' },
  { name: 'Raga', pin: '370415', gramPanchayat: 'Raga', category: 'Rural' },
  { name: 'Ramaniya', pin: '370405', gramPanchayat: 'Ramaniya', category: 'Rural' },
  { name: 'Ratadiya', pin: '370410', gramPanchayat: 'Ratadiya', category: 'Rural' },
  { name: 'Sadau', pin: '370421', gramPanchayat: 'Sadau', category: 'Rural' },
  { name: 'Samagoga', pin: '370415', gramPanchayat: 'Samagoga', category: 'Rural' },
  { name: 'Shekhadiya', pin: '370405', gramPanchayat: 'Shekhadiya', category: 'Rural' },
  { name: 'Shiracha', pin: '370435', gramPanchayat: 'Shiracha', category: 'Rural' },
  { name: 'Tappar', pin: '370425', gramPanchayat: 'Tappar', category: 'Rural' },
  { name: 'Toda', pin: '370415', gramPanchayat: 'Toda', category: 'Rural' },
  { name: 'Tumbadi Moti', pin: '370435', gramPanchayat: 'Tumbadi Moti', category: 'Rural' },
  { name: 'Tumbadi Nani', pin: '370435', gramPanchayat: 'Tumbadi Nani', category: 'Rural' },
  { name: 'Tunda', pin: '370435', gramPanchayat: 'Tunda', category: 'Industrial belt' },
  { name: 'Vadala', pin: '370410', gramPanchayat: 'Vadala', category: 'Rural' },
  { name: 'Vagura', pin: '370410', gramPanchayat: 'Vagura', category: 'Rural' },
  { name: 'Vanki', pin: '370425', gramPanchayat: 'Vanki', category: 'Rural' },
  { name: 'Viraniya', pin: '370425', gramPanchayat: 'Viraniya', category: 'Rural' },
  { name: 'Vovar', pin: '370410', gramPanchayat: 'Vovar', category: 'Rural' },
  { name: 'Mundra', pin: '370421', gramPanchayat: 'Mundra', category: 'Urban fringe' },
];

export const villageMasterSeed = villageNames.map((v, index) => {
  const geo = lookupOfficialVillageGeo(v.name, MUNDRA_BASE.talukaName);
  return {
    villageId: `V-MUN-${String(index + 1).padStart(3, '0')}`,
    villageName: v.name,
    talukaName: MUNDRA_BASE.talukaName,
    district: MUNDRA_BASE.district,
    state: MUNDRA_BASE.state,
    country: MUNDRA_BASE.country,
    gramPanchayat: v.gramPanchayat || v.name,
    pin: v.pin,
    category: v.category,
    latitude: geo?.latitude ?? null,
    longitude: geo?.longitude ?? null,
    boundary: geo?.boundary ?? null,
    active: MUNDRA_BASE.active,
  };
});

export function findVillageById(villages, villageId) {
  if (villageId == null || villageId === '') return null;
  const key = String(villageId);
  return (
    villages.find(
      (v) =>
        String(v.villageId) === key ||
        String(v.id) === key ||
        Number(v.id) === Number(villageId),
    ) || null
  );
}

export function villageLabel(v) {
  if (!v) return '—';
  return `${v.villageName} (${v.pin})`;
}
